import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
export interface Task {
  reservationId: string | null;
  reservationNumber: string | null;
  taskId: string;
  ruleId: string;
  code: string;
  label: string;
  description: string;
  triggerReason: string;
  windowStart: string | null;
  windowEnd: string | null;
  color: string;
  minutes: number;
  originalDueDate: string;
  serviceDate: string;
  state: "todo" | "declined" | "completed" | "superseded";
  overdue: boolean;
  lastActionStaffInitials: string | null;
  lastActionReason: string | null;
  lastEventId: string | null;
  completionEventId: string | null;
  coveredTaskIds: string[];
  allowedActions: string[];
}
export interface RoomDay {
  checkInAt: string | null;
  checkOutAt: string | null;
  date: string;
  stayDay: number | null;
  occupancy: string;
  guestNames: string[];
  readiness: string;
  readinessEvidenceAt: string | null;
  tasks: Task[];
  nextAction: { taskId: string; action: string; label: string } | null;
}
export interface Room {
  roomId: string;
  roomLabel: string;
  groupLabel: string;
  streamVersion: number;
  days: RoomDay[];
}
export interface Schedule {
  rules: Rule[];
  capabilities: { canManageSettings: boolean };
  from: string;
  days: number;
  asOf: string;
  timezone: string;
  settingsVersion: number;
  rooms: Room[];
  totals: {
    date: string;
    plannedCount: number;
    outstandingCount: number;
    completedCount: number;
    declinedCount: number;
    plannedMinutes: number;
    outstandingMinutes: number;
  }[];
}
export interface Rule {
  id: string;
  code: string;
  label: string;
  description: string;
  color: string;
  minutes: number;
  trigger: string;
  intervalNights: number | null;
  vacantArrivalNights: number | null;
  rank: number;
  coverageRuleIds: string[];
  retired: boolean;
}
export interface Settings {
  id: string;
  version: number;
  effectiveFrom: string;
  timezone: string;
  serviceDayBoundary: string;
  serviceStartDate: string | null;
  rules: Rule[];
}
export interface HistoryEvent {
  id: string;
  roomId: string;
  roomLabel: string;
  taskId: string;
  code: string;
  type: string;
  occurredAt: string;
  recordedAt: string;
  actorUserId: string;
  actorDisplayName: string;
  staffInitials: string | null;
  reason: string | null;
  referenceEventId: string | null;
  inspectionResult: string | null;
  streamVersion: number;
}
export interface Action {
  taskId: string;
  type: string;
  expectedStreamVersion: number;
  expectedSettingsVersion: number;
  idempotencyKey: string;
  occurredAt: string;
  staffInitials: string | null;
  reason: string | null;
  referenceEventId: string | null;
}
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}
export function propertyDate(
  now = new Date(),
  timezone = "Asia/Bangkok",
): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}
export function shiftDate(date: string, days: number): string {
  const value = new Date(`${date}T12:00:00Z`);
  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString().slice(0, 10);
}
export function taskBucket(
  task: Task,
): "overdue" | "todo" | "done" | "superseded" {
  if (task.state === "superseded") return "superseded";
  if (task.state === "completed") return "done";
  return task.overdue || task.originalDueDate < task.serviceDate
    ? "overdue"
    : "todo";
}
export function taskCounts(rooms: Room[]) {
  const tasks = rooms
    .flatMap((room) => room.days.flatMap((day) => day.tasks))
    .filter((task) => task.state !== "declined");
  return {
    overdue: tasks.filter((task) => taskBucket(task) === "overdue").length,
    todo: tasks.filter((task) => taskBucket(task) === "todo").length,
    done: tasks.filter((task) => taskBucket(task) === "done").length,
  };
}
export function actionBody(
  room: Room,
  task: Task,
  type: string,
  reason: string,
  initials: string,
  occurredAt: string,
  expectedSettingsVersion: number,
): Action {
  return {
    taskId: task.taskId,
    type,
    expectedStreamVersion: room.streamVersion,
    expectedSettingsVersion,
    idempotencyKey: crypto.randomUUID(),
    occurredAt,
    staffInitials: initials.trim() || null,
    reason: reason.trim() || null,
    referenceEventId: type === "reopen" ? (task.completionEventId ?? task.lastEventId) : null,
  };
}

export function chipInk(color: string): string {
  const match = /^#([0-9a-f]{6})$/i.exec(color);
  if (!match) return "#000000";
  const channels = [0, 2, 4]
    .map((i) => parseInt(match[1].slice(i, i + 2), 16) / 255)
    .map((v) =>
      v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4),
    );
  const luminance =
    channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
  return luminance > 0.179 ? "#000000" : "#ffffff";
}

export function localInput(value: Date, timezone: string): string {
  return formatInTimeZone(value, timezone, "yyyy-MM-dd'T'HH:mm");
}
export function utcInstant(value: string, timezone: string): string {
  return fromZonedTime(value, timezone).toISOString();
}
export function displayTime(value: string | null, timezone: string): string {
  if (!value) return "";
  return /^\d{2}:\d{2}/.test(value)
    ? value.slice(0, 5)
    : formatInTimeZone(new Date(value), timezone, "HH:mm");
}
export function displayInstant(value: string, timezone: string): string {
  return formatInTimeZone(new Date(value), timezone, "dd MMM yyyy HH:mm");
}

export function whiteBadgeFill(color: string): string {
  let hex = /^#[0-9a-f]{6}$/i.test(color) ? color : "#555555";
  for (let i = 0; i < 25 && chipInk(hex) === "#000000"; i++)
    hex =
      "#" +
      [1, 3, 5]
        .map((at) =>
          Math.floor(parseInt(hex.slice(at, at + 2), 16) * 0.94)
            .toString(16)
            .padStart(2, "0"),
        )
        .join("");
  return hex;
}

export function taskMatches(task: Task, state: string): boolean {
  return state === "all"
    ? true
    : state === "todo"
      ? taskBucket(task) === "todo"
      : state === "overdue"
        ? taskBucket(task) === "overdue"
        : state === "completed"
          ? taskBucket(task) === "done"
          : task.state === state;
}

export function roomDoorOrder<T extends { roomLabel: string }>(
  rooms: T[],
): T[] {
  return [...rooms].sort((a, b) =>
    a.roomLabel.localeCompare(b.roomLabel, undefined, {
      numeric: true,
      sensitivity: "base",
    }),
  );
}

// The front desk tree: room types alphabetically, doors in natural order within.
export function roomGroupOrder<T extends { roomLabel: string; groupLabel: string }>(
  rooms: T[],
): T[] {
  return roomDoorOrder(rooms).sort((a, b) =>
    a.groupLabel.localeCompare(b.groupLabel, undefined, { sensitivity: "base" }),
  );
}

// Paper room column: the door only, without a leading "Room " prefix.
export function paperRoomLabel(label: string): string {
  return label.replace(/^room\s+/i, "");
}

export const TRIGGER_LABELS: Record<string, string> = {
  occupiedInterval: "Every n nights occupied",
  vacantInterval: "After n nights empty",
  arrival: "On every check-in",
  departure: "On every check-out",
  turnaround: "Same-day departure and arrival",
};

export function triggerSentence(
  trigger: string,
  intervalNights: number | null,
): string {
  switch (trigger) {
    case "occupiedInterval":
      return `Every ${intervalNights ?? 1} nights a guest is in the room.`;
    case "vacantInterval":
      return `Every ${intervalNights ?? 1} nights a room stands empty, counted from the last checkout.`;
    case "arrival":
      return "On every check-in, when the room is already prepared.";
    case "departure":
      return "On every check-out with nobody arriving that day.";
    case "turnaround":
      return "Check-out and check-in on the same day, where the arriving guest stays the night.";
    default:
      return TRIGGER_LABELS[trigger] ?? trigger;
  }
}

export function absorbedBy(rules: Rule[], rule: Rule): Rule[] {
  return rules.filter(
    (other) =>
      other.id !== rule.id && (other.coverageRuleIds ?? []).includes(rule.id),
  );
}

export function declineLabel(day: RoomDay): string {
  return day.guestNames.length > 0 ? "Guest declined" : "Declined";
}

export function declineTitle(day: RoomDay): string {
  return day.guestNames.length > 0 ? "Guest declined the clean" : "Decline the clean";
}
